// The live notification-preferences route (bead hf-e7-notifications-742.3).
//
// Subscription, local edit state and mutation plumbing. Every word and every layout
// decision belongs to `NotificationPreferences.tsx`.
//
// ── WHY THERE IS LOCAL DRAFT STATE HERE AND NOWHERE ELSE IN THIS FEATURE ───────
//
// Preferences are the one surface in this product where a person makes SEVERAL related
// choices before any of them means anything. Saving each toggle as it is pressed would
// mean a member who is halfway to "email only, no practical updates" spends a moment
// in a state they did not choose — and, worse, that a wobble of the finger sends a real
// message somewhere they did not want it. So the draft is local and the save is
// explicit, and the server is the only place that decides whether the draft is legal.

import { Skeleton, StatusMessage } from "@herefor/design-system";
import { NOTIFICATION_PREFERENCE_MESSAGES as COPY } from "@herefor/product-language";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import {
  notificationPreferencesQuery,
  setCircleQuietHoursOverrideMutation,
  setNotificationPreferencesMutation,
} from "../../convex/notifications";
import { useConnectivity } from "../../offline/offline";
import {
  NotificationPreferences,
  type NotificationCategoryView,
  type NotificationChannel,
  type NotificationPreferencesView,
  type SaveState,
} from "./NotificationPreferences";

export function NotificationPreferencesRoute({ circleId }: { readonly circleId: string }) {
  const server = useQuery(notificationPreferencesQuery, { circleId });
  const savePreferences = useMutation(setNotificationPreferencesMutation);
  const saveCirclePolicy = useMutation(setCircleQuietHoursOverrideMutation);
  const { actionsPaused } = useConnectivity();

  const [draft, setDraft] = useState<NotificationPreferencesView | null>(null);
  const [adopted, setAdopted] = useState<NotificationPreferencesView | null>(null);
  const [state, setState] = useState<SaveState>({ status: "idle" });

  // ADOPT THE SERVER'S ANSWER WHENEVER IT CHANGES, including after our own save. The
  // server is what actually decides — it refuses a mute on an integrity category — so
  // a draft that outlived the response would show somebody a choice that was rejected.
  //
  // Adjusted DURING RENDER rather than in an effect, which is React's own guidance for
  // "reset state when a prop changes": an effect here re-renders the whole surface a
  // second time on every reactive update, and the linter is right to call that
  // cascading. The identity comparison is enough because Convex hands back a new object
  // only when the answer actually changed.
  if (server !== undefined && server !== adopted) {
    setAdopted(server);
    setDraft(server);
  }

  if (server === undefined || draft === null) {
    return <Skeleton label={COPY.loadingLabel} lines={6} />;
  }

  const busy = actionsPaused || state.status === "saving";

  const editCategory = (
    category: NotificationCategoryView["category"],
    change: (current: NotificationCategoryView) => NotificationCategoryView,
  ) => {
    setState({ status: "idle" });
    setDraft({
      ...draft,
      categories: draft.categories.map((row) => (row.category === category ? change(row) : row)),
    });
  };

  const save = () => {
    if (busy) return;
    setState({ status: "saving" });

    void savePreferences({
      circleId,
      // Only the mutable ones can be muted, and the server refuses the rest anyway —
      // this filter is so the browser never even ASKS for something it has been told
      // is not offered.
      mutedCategories: draft.categories
        .filter((row) => row.mutable && row.muted)
        .map((row) => row.category),
      categoryChannels: draft.categories.map((row) => ({
        category: row.category,
        channels: row.channels.filter(isChannel),
      })),
      ...(draft.quietHours === null ? { quietHours: null } : { quietHours: draft.quietHours }),
      idempotencyKey: crypto.randomUUID(),
    })
      .then((outcome) => {
        // `replayed` is the same save arriving twice — a retry of something that
        // already succeeded, which is success.
        if (outcome.status === "replayed" || outcome.value.ok) {
          setState({ status: "saved" });
          return;
        }
        setState({ status: "failed", message: COPY.saveFailed });
      })
      .catch(() => {
        // Fail-closed and VISIBLE (PLT-011). Somebody who believed they had set quiet
        // hours and had not would be woken by the product at 3am.
        setState({ status: "failed", message: COPY.saveFailed });
      });
  };

  return (
    <>
      {actionsPaused ? <StatusMessage tone="info">{COPY.saveFailed}</StatusMessage> : null}
      <NotificationPreferences
        actionsDisabled={actionsPaused}
        onQuietHoursChange={(quietHours) => {
          setState({ status: "idle" });
          setDraft({ ...draft, quietHours });
        }}
        onSave={save}
        onToggleChannel={(category, channel) => {
          editCategory(category, (row) => ({
            ...row,
            channels: row.channels.includes(channel)
              ? row.channels.filter((existing) => existing !== channel)
              : [...row.channels, channel],
          }));
        }}
        onToggleMuted={(category) => {
          editCategory(category, (row) => ({ ...row, muted: !row.muted }));
        }}
        preferences={draft}
        state={state}
        {...(draft.canManageCirclePolicy
          ? {
              // SAVED ON ITS OWN, and immediately. It is a decision about the circle
              // rather than about this person, it is separately capability-gated, and
              // burying it in a personal Save would make one button write two very
              // different kinds of record.
              onCirclePolicyChange: (allowed: boolean) => {
                void saveCirclePolicy({
                  circleId,
                  allowed,
                  idempotencyKey: crypto.randomUUID(),
                }).catch(() => {
                  setState({ status: "failed", message: COPY.saveFailed });
                });
              },
            }
          : {})}
      />
    </>
  );
}

function isChannel(value: string): value is NotificationChannel {
  return value === "sms" || value === "email";
}
